import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatNumber } from '../utils/helpers';
import { 
  Gauge, Building2, TrendingUp, AlertTriangle, RefreshCw, 
  Lock, Calendar, ChevronRight, RotateCcw, FileCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const MeteringPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fleetData, setFleetData] = useState(null);
  const [billingCycles, setBillingCycles] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteMetering, setSiteMetering] = useState(null);
  const [showSiteDetail, setShowSiteDetail] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Current month/year
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [fleetRes, cyclesRes, exceptionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/metering/fleet/${selectedYear}/${selectedMonth}`, { headers }),
        axios.get(`${BACKEND_URL}/api/metering/billing-cycles?year=${selectedYear}&month=${selectedMonth}`, { headers }),
        axios.get(`${BACKEND_URL}/api/metering/exceptions?year=${selectedYear}&month=${selectedMonth}`, { headers })
      ]);
      
      setFleetData(fleetRes.data);
      setBillingCycles(cyclesRes.data);
      setExceptions(exceptionsRes.data);
    } catch (error) {
      console.error('Failed to fetch metering data:', error);
      toast.error('Failed to load metering data');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewSiteDetail = async (siteId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${BACKEND_URL}/api/metering/sites/${siteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSiteMetering(res.data);
      setSelectedSite(siteId);
      setShowSiteDetail(true);
    } catch (error) {
      console.error('Failed to fetch site metering:', error);
      toast.error('Failed to load site details');
    }
  };

  const handleGenerateBillingDrafts = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/metering/billing-cycles/generate/${selectedYear}/${selectedMonth}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Generated ${res.data.generated} billing cycle drafts`);
      fetchData();
    } catch (error) {
      console.error('Failed to generate billing drafts:', error);
      toast.error('Failed to generate billing drafts');
    } finally {
      setGenerating(false);
    }
  };

  const handleLockCycle = async (cycleId) => {
    if (!window.confirm('Lock this billing cycle? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/metering/billing-cycles/${cycleId}/lock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Billing cycle locked');
      fetchData();
    } catch (error) {
      console.error('Failed to lock cycle:', error);
      toast.error(error.response?.data?.detail || 'Failed to lock cycle');
    }
  };

  const getQualityBadge = (quality) => {
    const badges = {
      good: { color: 'bg-green-100 text-green-700', label: 'Good' },
      estimated: { color: 'bg-yellow-100 text-yellow-700', label: 'Estimated' },
      missing: { color: 'bg-red-100 text-red-700', label: 'Missing' },
      reset_detected: { color: 'bg-orange-100 text-orange-700', label: 'Reset' }
    };
    const badge = badges[quality] || badges.good;
    return <span className={`px-2 py-1 rounded text-xs ${badge.color}`}>{badge.label}</span>;
  };

  const getMethodBadge = (method) => {
    if (method === 'cumulative') {
      return <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">Cumulative</span>;
    }
    return <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-700">Interval</span>;
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <PageHeader
              title="BESS Metering"
              description="Cumulative discharge metering and billing"
            />
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-white border rounded-sm px-3 py-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <select 
                  value={selectedMonth} 
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="border-none bg-transparent focus:outline-none text-sm"
                >
                  {monthNames.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select 
                  value={selectedYear} 
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="border-none bg-transparent focus:outline-none text-sm"
                >
                  {[2024, 2025, 2026, 2027].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <Button onClick={handleGenerateBillingDrafts} disabled={generating} className="bg-slate-900">
                <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                {generating ? 'Generating...' : 'Generate Billing'}
              </Button>
            </div>
          </div>

          {/* Fleet Summary Cards */}
          {fleetData && (
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-slate-200 rounded-sm p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Total Sites</p>
                    <p className="text-2xl font-bold text-slate-900">{fleetData.total_sites}</p>
                  </div>
                  <Building2 className="w-8 h-8 text-slate-300" />
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Monthly Units</p>
                    <p className="text-2xl font-bold text-blue-600">{formatNumber(fleetData.total_monthly_units_kwh)} kWh</p>
                  </div>
                  <Gauge className="w-8 h-8 text-blue-200" />
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Lifetime Units</p>
                    <p className="text-2xl font-bold text-green-600">{formatNumber(fleetData.total_lifetime_units_kwh)} kWh</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-200" />
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Data Issues</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {fleetData.sites_with_missing_data + fleetData.sites_with_resets + fleetData.sites_with_estimated_billing}
                    </p>
                    <p className="text-xs text-slate-500">
                      {fleetData.sites_with_missing_data} missing, {fleetData.sites_with_resets} resets, {fleetData.sites_with_estimated_billing} estimated
                    </p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-200" />
                </div>
              </div>
            </div>
          )}

          <Tabs defaultValue="sites" className="mt-6">
            <TabsList className="bg-white border border-slate-200 p-1">
              <TabsTrigger value="sites" className="text-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                <Building2 className="w-4 h-4 mr-2" /> Site Metering
              </TabsTrigger>
              <TabsTrigger value="billing" className="text-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                <FileCheck className="w-4 h-4 mr-2" /> Billing Cycles
              </TabsTrigger>
              <TabsTrigger value="exceptions" className="text-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                <AlertTriangle className="w-4 h-4 mr-2" /> Exceptions ({exceptions.length})
              </TabsTrigger>
            </TabsList>

            {/* Site Metering Tab */}
            <TabsContent value="sites" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Site</th>
                        <th>Lifetime Units</th>
                        <th>Monthly Units</th>
                        <th>Method</th>
                        <th>Quality</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fleetData?.site_details?.map((site) => (
                        <tr key={site.site_id}>
                          <td className="font-medium">{site.site_name}</td>
                          <td>{formatNumber(site.lifetime_units_kwh)} kWh</td>
                          <td className="font-semibold">{formatNumber(site.monthly_units_kwh)} kWh</td>
                          <td>{getMethodBadge(site.calculation_method)}</td>
                          <td>{getQualityBadge(site.quality_flag)}</td>
                          <td>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleViewSiteDetail(site.site_id)}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Billing Cycles Tab */}
            <TabsContent value="billing" className="mt-4">
              {billingCycles.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
                  <FileCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No billing cycles for this period.</p>
                  <p className="text-sm text-slate-500 mt-1">Click "Generate Billing" to create drafts.</p>
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Site</th>
                          <th>Period</th>
                          <th>Opening</th>
                          <th>Closing</th>
                          <th>Billable Units</th>
                          <th>Method</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingCycles.map((cycle) => (
                          <tr key={cycle.cycle_id}>
                            <td className="font-medium">{cycle.site_name}</td>
                            <td>{cycle.year}-{String(cycle.month).padStart(2, '0')}</td>
                            <td>{cycle.opening_reading ? `${formatNumber(cycle.opening_reading)} kWh` : '-'}</td>
                            <td>{cycle.closing_reading ? `${formatNumber(cycle.closing_reading)} kWh` : '-'}</td>
                            <td className="font-semibold">{formatNumber(cycle.billable_units_kwh)} kWh</td>
                            <td>{getMethodBadge(cycle.calculation_method)}</td>
                            <td>
                              {cycle.is_locked ? (
                                <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700 flex items-center w-fit">
                                  <Lock className="w-3 h-3 mr-1" /> Locked
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded text-xs bg-slate-100 text-slate-700">Draft</span>
                              )}
                            </td>
                            <td>
                              {!cycle.is_locked && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleLockCycle(cycle.cycle_id)}
                                >
                                  <Lock className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Exceptions Tab */}
            <TabsContent value="exceptions" className="mt-4">
              {exceptions.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
                  <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No billing exceptions for this period.</p>
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Site</th>
                          <th>Period</th>
                          <th>Billable Units</th>
                          <th>Issues</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exceptions.map((ex) => (
                          <tr key={ex.cycle_id} className="bg-orange-50">
                            <td className="font-medium">{ex.site_name}</td>
                            <td>{ex.year}-{String(ex.month).padStart(2, '0')}</td>
                            <td>{formatNumber(ex.billable_units_kwh)} kWh</td>
                            <td>
                              <div className="space-y-1">
                                {ex.exception_reasons.map((reason, i) => (
                                  <span key={`${reason}-${i}`} className="block text-xs text-orange-700">• {reason}</span>
                                ))}
                              </div>
                            </td>
                            <td>
                              {ex.is_locked ? (
                                <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">Locked</span>
                              ) : (
                                <span className="px-2 py-1 rounded text-xs bg-orange-100 text-orange-700">Needs Review</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Site Detail Modal */}
      <Dialog open={showSiteDetail} onOpenChange={setShowSiteDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{siteMetering?.site_name} - Metering Details</DialogTitle>
            <DialogDescription>Cumulative discharge metering data</DialogDescription>
          </DialogHeader>
          
          {siteMetering && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-sm p-4">
                  <p className="text-xs text-slate-500 uppercase">Lifetime Units</p>
                  <p className="text-xl font-bold text-slate-900">{formatNumber(siteMetering.lifetime_units_kwh)} kWh</p>
                </div>
                <div className="bg-blue-50 rounded-sm p-4">
                  <p className="text-xs text-slate-500 uppercase">This Month</p>
                  <p className="text-xl font-bold text-blue-600">{formatNumber(siteMetering.this_month?.billable_units_kwh || 0)} kWh</p>
                </div>
                <div className="bg-slate-50 rounded-sm p-4">
                  <p className="text-xs text-slate-500 uppercase">Last Month</p>
                  <p className="text-xl font-bold text-slate-600">{formatNumber(siteMetering.last_month?.billable_units_kwh || 0)} kWh</p>
                </div>
              </div>

              {/* Current Month Details */}
              <div className="border rounded-sm p-4">
                <h4 className="font-medium mb-3">Current Month ({siteMetering.this_month?.year}-{String(siteMetering.this_month?.month).padStart(2, '0')})</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Opening Reading</p>
                    <p className="font-medium">
                      {siteMetering.this_month?.opening_reading 
                        ? `${formatNumber(siteMetering.this_month.opening_reading.normalized_value)} kWh`
                        : 'N/A'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {siteMetering.this_month?.opening_reading?.timestamp?.slice(0, 19) || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Closing Reading</p>
                    <p className="font-medium">
                      {siteMetering.this_month?.closing_reading 
                        ? `${formatNumber(siteMetering.this_month.closing_reading.normalized_value)} kWh`
                        : 'N/A'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {siteMetering.this_month?.closing_reading?.timestamp?.slice(0, 19) || '-'}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex items-center space-x-3">
                  <span className="text-sm text-slate-500">Calculation:</span>
                  {getMethodBadge(siteMetering.this_month?.calculation_method)}
                  {getQualityBadge(siteMetering.this_month?.quality_flag)}
                </div>
              </div>

              {/* Meter Info */}
              <div className="border rounded-sm p-4">
                <h4 className="font-medium mb-3">Meter Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Timezone</p>
                    <p className="font-medium">{siteMetering.timezone}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Metering Mode</p>
                    <p className="font-medium capitalize">{siteMetering.metering_mode}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Commissioning Baseline</p>
                    <p className="font-medium">{formatNumber(siteMetering.commissioning_cum_discharge_kwh)} kWh</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Meter Resets</p>
                    <p className="font-medium flex items-center">
                      {siteMetering.reset_count}
                      {siteMetering.reset_count > 0 && <RotateCcw className="w-4 h-4 ml-2 text-orange-500" />}
                    </p>
                  </div>
                </div>
              </div>

              {/* Latest Reading */}
              <div className="border rounded-sm p-4 bg-slate-50">
                <h4 className="font-medium mb-2">Latest Reading</h4>
                <div className="text-sm">
                  <p className="text-slate-500">
                    {siteMetering.latest_reading?.timestamp?.slice(0, 19) || 'No data'}
                  </p>
                  <p className="font-medium text-lg">
                    {siteMetering.latest_reading?.normalized_cumulative_kwh 
                      ? `${formatNumber(siteMetering.latest_reading.normalized_cumulative_kwh)} kWh`
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeteringPage;
